/**
 * Session Storage Service for JWT Token Management
 * Implements the architecture shown in the diagram where JWT is stored in browser session storage
 */
import type { UserProfile } from '../models/userProfile';
import { UserProfileCacheService } from './UserProfileCacheService';

export class SessionStorageService {
  private static readonly JWT_TOKEN_KEY = 'jwt_token';
  private static readonly USER_INFO_KEY = 'user_info';
  private static readonly AUTH_STATE_KEY = 'auth_state';

  /**
   * Store JWT token in session storage
   */
  static setToken(token: string): void {
    try {
      sessionStorage.setItem(this.JWT_TOKEN_KEY, token);
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Retrieve JWT token from session storage
   */
  static getToken(): string | null {
    try {
      return sessionStorage.getItem(this.JWT_TOKEN_KEY);
    } catch (_error) {
      return null;
    }
  }

  /**
   * Remove JWT token from session storage
   */
  static removeToken(): void {
    try {
      sessionStorage.removeItem(this.JWT_TOKEN_KEY);
      sessionStorage.removeItem(this.USER_INFO_KEY);
      sessionStorage.removeItem(this.AUTH_STATE_KEY);
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Store user information
   */
  static setUserInfo(userInfo: UserProfile): void {
    try {
      sessionStorage.setItem(this.USER_INFO_KEY, JSON.stringify(userInfo));
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Retrieve user information
   */
  static getUserInfo(): UserProfile | null {
    try {
      const userInfo = sessionStorage.getItem(this.USER_INFO_KEY);
      return userInfo ? JSON.parse(userInfo) : null;
    } catch (_error) {
      return null;
    }
  }

  /**
   * Check if token exists in session storage
   */
  static hasToken(): boolean {
    return this.getToken() !== null;
  }

  /**
   * Clear all authentication data from session storage
   * This should be called on logout to clear all cached data
   */
  static clearAll(): void {
    try {
      this.removeToken();
      
      // Clear user email and login status
      sessionStorage.removeItem('userEmail');
      sessionStorage.removeItem('isLoggedIn');
      
      UserProfileCacheService.clearCache();
    } catch {}
  }

  static setAuthState(state: Record<string, unknown>): void {
    try {
      sessionStorage.setItem(this.AUTH_STATE_KEY, JSON.stringify(state));
    } catch {}
  }

  static getAuthState(): Record<string, unknown> | null {
    try {
      const state = sessionStorage.getItem(this.AUTH_STATE_KEY);
      return state ? JSON.parse(state) : null;
    } catch {
      return null;
    }
  }
}
